import React, {FunctionComponent, useState} from 'react';
import {IconButton, Menu, MenuItem, TableCell, TableRow, Tooltip} from '@material-ui/core';
import {Business} from '@material-ui/icons';
import {OwnerType} from '@treez/commons/sharedTypings/product';
import {Color, ColorName} from '@treez-ui/core/styles/Color';
import {
    AssignIcon,
    CheckIcon,
    CloseIcon,
    EditIcon,
    OptionsIcon,
    StoreIcon,
    UndoIcon,
} from '@treez-ui/core/icons';
import {PriceTierWithCatalogPresence} from '~/lib/sharedInterfaces';

const {ORG, STORE} = OwnerType;

interface PriceTierRowProps {
    onActivate?: (tier: PriceTierWithCatalogPresence) => void;
    onConvert?: (tier: PriceTierWithCatalogPresence) => void;
    onDeactivate?: (tier: PriceTierWithCatalogPresence) => void;
    onEditTier?: (tier: PriceTierWithCatalogPresence) => void;
    onReassign?: (tier: PriceTierWithCatalogPresence) => void;
    priceTier: PriceTierWithCatalogPresence;
    readOnly: boolean;
}

const MAX_NUM_OF_STORES_SHOWN = 5;

export const PriceTierRow: FunctionComponent<PriceTierRowProps> = (props: PriceTierRowProps) => {
    const {
        onActivate,
        onConvert,
        onDeactivate,
        onEditTier,
        onReassign,
        priceTier,
        readOnly = false,
    } = props;

    const [optionsAnchorElement, setOptionsAnchorElement] = useState<EventTarget>();

    const handleOptionsClick = (event: React.MouseEvent<HTMLElement>) => {
        setOptionsAnchorElement(event.target);
    };

    const handleOptionsClose = () => {
        setOptionsAnchorElement(undefined);
    };

    const handleAction = (action: (tier: PriceTierWithCatalogPresence) => void) => {
        action(priceTier);
        handleOptionsClose();
    };

    const renderOwnerTypeIcon = (ownerType: OwnerType) => {
        switch (ownerType) {
            case ORG:
                return <Business />;
            case STORE:
                return <StoreIcon />;
            default:
        }
    };

    const getAssociatedStores = (priceTier: PriceTierWithCatalogPresence) => {
        if (priceTier.catalogPresence.length === 0) {
            return null;
        }

        const storeNames = priceTier.catalogPresence.sort();

        if (priceTier.catalogPresence.length > MAX_NUM_OF_STORES_SHOWN) {
            const storeNamesString = storeNames.join(', ');
            const storeNamesLimited = storeNames.slice(0, MAX_NUM_OF_STORES_SHOWN).join(', ');

            return (
                <Tooltip arrow title={storeNamesString} placement="top">
                    <div>{storeNamesLimited}, ...</div>
                </Tooltip>
            );
        }

        return <div>{storeNames.join(', ')}</div>;
    };

    return (
        <TableRow>
            <TableCell>{priceTier.label}</TableCell>
            <TableCell>{priceTier.method}</TableCell>
            <TableCell>
                <div className="aligned">
                    {renderOwnerTypeIcon(priceTier.ownerType)}
                    <div className="price-tier-owner-type">{priceTier.ownerType}</div>
                </div>
            </TableCell>
            <TableCell>{getAssociatedStores(priceTier)}</TableCell>
            <TableCell>
                <span
                    style={{
                        backgroundColor: priceTier.isActive
                            ? Color[ColorName.GREEN_LIGHT]
                            : Color[ColorName.GREY_MIDDLE],
                    }}
                    className="price-tier-status"
                >
                    {priceTier.isActive ? 'Active' : 'Inactive'}
                </span>
            </TableCell>
            {readOnly ? null : (
                <TableCell>
                    <IconButton onClick={handleOptionsClick}>
                        <OptionsIcon />
                    </IconButton>
                    <Menu
                        anchorEl={optionsAnchorElement as HTMLElement}
                        onClose={handleOptionsClose}
                        open={Boolean(optionsAnchorElement)}
                    >
                        <MenuItem onClick={() => handleAction(onEditTier!)}>
                            <IconButton>
                                <EditIcon />
                            </IconButton>
                            Edit
                        </MenuItem>
                        <MenuItem onClick={() => handleAction(onReassign!)}>
                            <IconButton>
                                <AssignIcon />
                            </IconButton>
                            Reassign
                        </MenuItem>
                        {priceTier.ownerType === STORE ? (
                            <MenuItem onClick={() => handleAction(onConvert!)}>
                                <IconButton>
                                    <UndoIcon />
                                </IconButton>
                                Convert
                            </MenuItem>
                        ) : null}
                        {priceTier.isActive ? (
                            <MenuItem onClick={() => handleAction(onDeactivate!)}>
                                <IconButton>
                                    <CloseIcon />
                                </IconButton>
                                Deactivate
                            </MenuItem>
                        ) : (
                            <MenuItem onClick={() => handleAction(onActivate!)}>
                                <IconButton>
                                    <CheckIcon />
                                </IconButton>
                                Activate
                            </MenuItem>
                        )}
                    </Menu>
                </TableCell>
            )}
        </TableRow>
    );
};
